import { Avatar, Clickable, Col, Input, Row, Text } from 'components';
import { useOutsideClick } from 'core';
import { Api, sports, theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const UserWrapper = styled.div`
  position: absolute;
  background-color: #fff;
  min-height: 60px;
  max-height: 240px;
  overflow-y: auto;
  top: 5px;
  width: 100%;
  padding: 4px 0;
  border-radius: 16px;
  z-index: 10;
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.2);

  .add-event-user-item {
    border-bottom: 1px solid ${theme.GREY_20};
  }

  > :last-child .add-event-user-item {
    border-bottom: none;
  }

  .add-event-user-item > :first-child {
    flex-shrink: 0;
  }

  /* Name over @handle, each one line: in a narrow picker the two used to share a row,
     so the name wrapped word by word and the handle was cut off mid-word. */
  .add-event-user-text {
    min-width: 0;
  }

  .add-event-user-text > * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

let timer,
  scrolled = false;
const SearchUsers = ({
  sport,
  type,
  handleSelect,
  userType,
  selected,
  exclude,
  title,
  noRounded,
  error,
  placeholder,
  includeDummy,
  wid,
  disabled
}) => {
  const [searching, setSearching] = useState(false),
    [results, setResults] = useState(null),
    [text, setText] = useState(''),
    [listEndReached, setListEndReached] = useState(false),
    inputRef = useRef(),
    scrollRef = useRef(),
    foundSport = sports.find((x) => x.value === sport);

  useEffect(() => {
    if (!scrollRef?.current) return;

    scrollRef?.current.addEventListener('scroll', onScroll);
    return () => scrollRef?.current.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  useOutsideClick(inputRef, () => setSearching(false), ['users-wrapper']);

  async function onScroll(e) {
    if (scrolled) return;

    const bottomThreshold = e.target.scrollHeight - e.target.clientHeight,
      scrollHt = e.target.scrollTop + 200;

    if (scrollHt <= bottomThreshold && !scrolled) return;
    scrolled = true;

    (await !listEndReached) && handleSearch(text);
    scrolled = false;
  }

  async function handleSearch(value, searchNew) {
    if (disabled) return;

    setText(value);
    clearTimeout(timer);

    timer = setTimeout(async () => {
      setListEndReached(false);

      if (!value.length) {
        setResults([]);
        setSearching(false);
        return;
      }

      const res = await Api.post('/users/search', {
        text: value,
        type: userType?.toLowerCase(),
        sports: sport ? [sport.toLowerCase()] : undefined,
        exclude,
        skip: (!searchNew && results?.length) || 0,
        includeSelf: true,
        includeDummy: includeDummy === true
      });
      if (!res) return;
      if (!res?.length) return setListEndReached(true);

      setSearching(true);
      setResults(searchNew ? res : [...results, ...res]);
    }, 300);
  }

  function onSelect(user) {
    handleSelect(user);
    setText('');
    setSearching(false);
    setResults(null);
  }

  return (
    <Col
      marg={!selected && !noRounded && '0 0 20px'}
      wid={!selected && (wid || '100%')}
      noFlex
      // Dims the whole search box while it is switched off, so it reads as unavailable
      // rather than as an empty field the user is expected to type in.
      style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
    >
      {!selected && (
        <Input
          title={title}
          onChange={(e) => handleSearch(e.target.value, true)}
          inputRef={inputRef}
          onFocus={() => !disabled && results?.length && setSearching(true)}
          value={text}
          disabled={disabled}
          placeholder={
            placeholder || !userType
              ? 'Search users...'
              : userType?.toLowerCase() === 'team'
                ? 'Search teams...'
                : 'Search athletes...'
          }
          className="search-input"
          big
          search
          rounded={!noRounded}
          bg="#fff"
          error={error?.err}
        />
      )}

      {searching && (
        <Row pos="relative" ht="0" start>
          <UserWrapper className="users-wrapper" ref={scrollRef}>
            {results?.map((x, i) => {
              if (exclude?.includes(x._id)) return null;

              return (
                <Clickable key={i} onClick={() => onSelect(x)}>
                  <Row className="add-event-user-item" pad="8px 16px" center>
                    <Avatar user={x} small />

                    <Col className="add-event-user-text" marg="0 0 0 10px">
                      <Text med title={x.name}>
                        {x.name}
                      </Text>
                      {!x.dummy && (
                        <Text b2 col={theme.GREY_60} title={`@${x.username}`}>
                          @{x.username}
                        </Text>
                      )}
                    </Col>
                  </Row>
                </Clickable>
              );
            })}
          </UserWrapper>
        </Row>
      )}
    </Col>
  );
};

export default SearchUsers;
